import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Toast } from "../../components/Toast";

type ValidationState =
  | { status: "empty" }
  | { status: "valid" }
  | { status: "invalid"; line?: number; column?: number };

type ParseResult =
  | { data: unknown }
  | { error: { line?: number; column?: number } };

const parseJson = (value: string): ParseResult => {
  try {
    return { data: JSON.parse(value) };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const lineColumnMatch = message.match(/line\s+(\d+)\s+column\s+(\d+)/i);
    if (lineColumnMatch) {
      return {
        error: {
          line: Number(lineColumnMatch[1]),
          column: Number(lineColumnMatch[2]),
        },
      };
    }

    const positionMatch = message.match(/position\s+(\d+)/i);
    if (positionMatch) {
      const position = Number(positionMatch[1]);
      if (!Number.isNaN(position)) {
        const lines = value.slice(0, position).split("\n");
        return {
          error: {
            line: lines.length,
            column: lines[lines.length - 1]?.length + 1,
          },
        };
      }
    }

    return { error: {} };
  }
};

export function JsonFormatter() {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [validation, setValidation] = useState<ValidationState>({ status: "empty" });
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  useEffect(() => {
    const trimmed = input.trim();
    if (!trimmed) {
      setValidation({ status: "empty" });
      return;
    }

    const result = parseJson(input);
    if ("error" in result) {
      setValidation({ status: "invalid", ...result.error });
      return;
    }

    setValidation({ status: "valid" });
  }, [input]);

  const formatJson = (mode: "beautify" | "minify") => {
    const trimmed = input.trim();
    if (!trimmed) {
      setOutput("");
      setValidation({ status: "empty" });
      return;
    }

    const result = parseJson(input);
    if ("error" in result) {
      setValidation({ status: "invalid", ...result.error });
      return;
    }

    const indent = mode === "beautify" ? 2 : 0;
    setOutput(JSON.stringify(result.data, null, indent));
    setValidation({ status: "valid" });
  };

  const copyOutput = async () => {
    if (!output) {
      return;
    }

    try {
      await navigator.clipboard.writeText(output);
      setToastMessage(t("jsonFormatter.copied"));
    } catch {
      setToastMessage(t("jsonFormatter.copyFailed"));
    }
  };

  const clearAll = () => {
    setInput("");
    setOutput("");
    setValidation({ status: "empty" });
  };

  const renderValidationMessage = () => {
    if (validation.status === "empty") {
      return null;
    }
    if (validation.status === "valid") {
      return <p className="jf-status jf-status-valid">{t("jsonFormatter.valid")}</p>;
    }

    const hasPosition = validation.line && validation.column;
    return (
      <p className="jf-status jf-status-error">
        {t("jsonFormatter.invalid")}
        {hasPosition && (
          <span className="jf-status-position">
            {t("jsonFormatter.errorPosition", {
              line: validation.line,
              column: validation.column,
            })}
          </span>
        )}
      </p>
    );
  };

  return (
    <div className="json-formatter">
      <div className="jf-grid">
        <div className="jf-card">
          <label className="jf-label" htmlFor="jf-input">
            {t("jsonFormatter.inputLabel")}
          </label>
          <textarea
            id="jf-input"
            className="jf-textarea"
            placeholder={t("jsonFormatter.inputPlaceholder")}
            value={input}
            onChange={(event) => setInput(event.target.value)}
          />
          <div className="jf-actions">
            <button
              type="button"
              className="jf-primary-btn"
              onClick={() => formatJson("beautify")}
              disabled={!input.trim()}
            >
              {t("jsonFormatter.beautify")}
            </button>
            <button
              type="button"
              className="jf-secondary-btn"
              onClick={() => formatJson("minify")}
              disabled={!input.trim()}
            >
              {t("jsonFormatter.minify")}
            </button>
          </div>
          {renderValidationMessage()}
        </div>

        <div className="jf-card">
          <label className="jf-label" htmlFor="jf-output">
            {t("jsonFormatter.outputLabel")}
          </label>
          <textarea
            id="jf-output"
            className="jf-textarea jf-textarea-output"
            placeholder={t("jsonFormatter.outputPlaceholder")}
            value={output}
            readOnly
          />
          <div className="jf-actions">
            <button
              type="button"
              className="jf-secondary-btn"
              onClick={copyOutput}
              disabled={!output}
            >
              {t("jsonFormatter.copy")}
            </button>
            <button
              type="button"
              className="jf-ghost-btn"
              onClick={clearAll}
              disabled={!input && !output}
            >
              {t("jsonFormatter.clear")}
            </button>
          </div>
        </div>
      </div>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
